import type { Icon } from '@phosphor-icons/react';
import { ArrowRight, BookOpenText, GameController, PencilSimple } from '@phosphor-icons/react';
import RedesignImage from '../../shared/components/RedesignImage';
import { homepageImages } from '../assets';
import { useHomePageSharedStyles } from '../HomePage.styles';
import { ResourceCounts, UseHomePageReturn } from '../hooks/useHomePage';
import locale from '../locale.json';
import { useHomePageEvolutionToolsSectionStyles } from './HomePageEvolutionToolsSection.styles';

type HomePageEvolutionToolsSectionProps = {
  resourceCounts: ResourceCounts;
  onNavigate: UseHomePageReturn['redirect'];
};

const TOOL_CARDS: {
  key: string;
  badge: string;
  badgeIcon: Icon;
  title: string;
  description: string;
  countKey: keyof ResourceCounts;
  countLabel: string;
  cta: string;
  image: string;
  tabIndex: number;
  resourceTab?: 'seriousgame' | 'ebook';
}[] = [
  {
    key: 'tests',
    badge: 'Tests',
    badgeIcon: PencilSimple,
    title: 'Mieux me connaître',
    description: 'Tests psychométriques et d’aptitudes pour explorer vos talents et valeurs.',
    countKey: 'tests',
    countLabel: 'tests disponibles',
    cta: 'Accéder',
    image: homepageImages.tools.tests,
    tabIndex: 1,
  },
  {
    key: 'games',
    badge: 'Serious games',
    badgeIcon: GameController,
    title: 'M’entraîner en situation',
    description: 'Simulations immersives pour vous exercer en conditions réelles et développer vos compétences relationnelles et décisionnelles.',
    countKey: 'games',
    countLabel: 'serious games disponibles',
    cta: 'Jouer',
    image: homepageImages.tools.seriousGames,
    tabIndex: 3,
    resourceTab: 'seriousgame',
  },
  {
    key: 'ebooks',
    badge: 'Ebooks & guides',
    badgeIcon: BookOpenText,
    title: 'M’aider à avancer',
    description: 'Guides et ebooks pour gérer vos émotions et vos périodes de transition',
    countKey: 'ebooks',
    countLabel: 'ebooks disponibles',
    cta: 'Lire',
    image: homepageImages.tools.ebooks,
    tabIndex: 3,
    resourceTab: 'ebook',
  },
];

const HomePageEvolutionToolsSection = ({
  resourceCounts,
  onNavigate,
}: HomePageEvolutionToolsSectionProps) => {
  const shared = useHomePageSharedStyles();
  const local = useHomePageEvolutionToolsSectionStyles();
  const classes = { ...shared, ...local };

  return (
    <section className={classes.sectionBeige}>
      <div className={classes.sectionIntro}>
        <p className={classes.sectionLabel}>{locale.evolutionLabel}</p>
        <h2 className={classes.sectionTitle}>{locale.evolutionTitle}</h2>
        <p className={classes.sectionDescription}>{locale.evolutionDescription}</p>
      </div>

      <div className={classes.toolsGrid}>
        {TOOL_CARDS.map(card => {
          const count = resourceCounts[card.countKey];

          return (
            <article key={card.key} className={classes.toolCard}>
              <div className={classes.toolImageWrap}>
                <RedesignImage src={card.image} alt="" className={classes.cardImage} />
                <div className={classes.toolBadge}>
                  <card.badgeIcon className={classes.toolBadgeIcon} aria-hidden="true" />
                  {card.badge}
                </div>
              </div>

              <div className={classes.toolBody}>
                <div className={classes.toolTextBlock}>
                  <h3 className={classes.toolTitle}>{card.title}</h3>
                  <p className={classes.toolDescription}>{card.description}</p>
                </div>

                <div className={classes.toolFooter}>
                  {count !== null && (
                    <div className={classes.toolCountRow}>
                      <span className={classes.toolCountBadge}>{count}</span>
                      <p className={classes.toolCountLabel}>{card.countLabel}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    className={classes.textLinkButton}
                    onClick={() => onNavigate(card.tabIndex, card.resourceTab)}>
                    {card.cta}
                    <ArrowRight className={classes.arrowIcon} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default HomePageEvolutionToolsSection;
